import React, {useContext} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {HotelCardContext} from 'components/HotelCardContext'
import {UnavailabilityDesktop} from 'components/Unavailability/Desktop'
import {useGetHotelAvailabilityForCurrentSearch} from 'hooks/useGetHotelAvailabilityForCurrentSearch'
import {
  getAnchorComplete,
  getHotelHasOffers,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {setActiveHotelId} from 'modules/search/slice'

import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'

import {SelectedElement} from '../../utils/selectedElement'
import {AccommodationPageLink} from './AccommodationPageLink'
import {AnchorLabel} from './AnchorLabel'
import {BaseHotelCard} from './BaseHotelCard'
import {Gallery} from './Gallery'
import {OfferList} from './OfferList'
import {PropertySummary} from './PropertySummary/PropertySummary'
import {TopOffer} from './TopOfferInfo/TopOffer'

interface Props {
  shouldShowDivider?: boolean
  isPriorityCard?: boolean
}

export const HotelCardDesktop: React.FC<Props> = ({
  shouldShowDivider = true,
  isPriorityCard = false
}) => {
  const dispatch = useDispatch()
  const {isAnchor, hotelId} = useContext(HotelCardContext)

  const hotelHasOffers = useSelector(state => getHotelHasOffers(state, hotelId))
  const showDivider = !isAnchor && shouldShowDivider
  const anchorComplete = useSelector(getAnchorComplete)
  const {hasSplitBookingOffer} = useSelector(state =>
    getSplitBookingDetails(state, hotelId)
  )

  const onMouseEnter = () => dispatch(setActiveHotelId(hotelId))
  const onMouseLeave = () => dispatch(setActiveHotelId(null))

  useGetHotelAvailabilityForCurrentSearch({isAnchor, hotelId})

  const shouldShowAvailabilityPane =
    anchorComplete && isAnchor && !hotelHasOffers && !hasSplitBookingOffer

  return (
    <Box
      data-id={isAnchor ? 'anchorHotel' : 'nonAnchorHotel'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      marginRight="auto"
      marginBottom={isAnchor ? 's500' : 's000'}
    >
      <div data-id={SelectedElement.BlankSpace}>
        <BaseHotelCard
          isAnchor={isAnchor}
          galleryComponent={
            <AccommodationPageLink>
              <Gallery isPriorityCard={isPriorityCard} />
            </AccommodationPageLink>
          }
          propertySummaryComponent={
            <AccommodationPageLink>
              <PropertySummary />
            </AccommodationPageLink>
          }
          topOfferComponent={<TopOffer />}
          offerListComponent={<OfferList />}
          availabilityPaneComponent={
            shouldShowAvailabilityPane ? (
              <UnavailabilityDesktop
                sourceComponentPrefix={'HotelCardDesktop'}
              />
            ) : null
          }
          anchorLabelComponent={isAnchor && <AnchorLabel />}
        />
        {showDivider && (
          <Box paddingTop="s300" paddingBottom="s300">
            <Divider sideMargin="s400" />
          </Box>
        )}
      </div>
    </Box>
  )
}
